import { HorizontalAlignment, OpenUrlWithKind, ReceptionVisitInput } from '@api/generated/models';
import { LegacyText, Spacer, Text } from '@cellula/react';
import { tokens } from '@cellula/react-theme-patient';
import { ExternalSvg } from '@components/external_svg.component';
import { Icon, IconType } from '@components/icon.component';
import { Shimmer } from '@components/shimmer.component';
import { VStack } from '@components/v_stack.component';
import { getAbsoluteRoute } from '@helpers/route';
import { withTranslation } from '@modules/i18n_and_l10n/with_translation';
import type { Disposer } from '@modules/state/state';
import { AuthService } from '@services/auth/auth.service';
import type { FlexibleRecentVisitsModel } from '@services/flexible_component/mod';
import { FlexibleButtonIconModel } from '@services/flexible_component/mod';
import { VisibaEnvelope } from '@services/http.service';
import { theme } from '@services/theme.service/theme';
import { UnitService } from '@services/unit.service';
import { RecentReceptionsApiService } from '@src/api/recent_receptions_api.service';
import { Scoped, useService } from '@visiba/instantiation';
import { Presentation, usePresentation } from '@visiba/presentation';
import { rem } from '@visiba/std';
import { Fragment, Suspense, useEffect } from 'react';
import styled from 'styled-components';
import { Routeable } from './_flexible_identifier_routeable.component';

interface Props {
	component: FlexibleRecentVisitsModel;
}

const elements = {
	container: styled.div`
		margin: ${rem(8)} 0;
		display: flex;
		flex-direction: column;
	`,
	fixedSizeContainer: styled.div`
		min-height: ${rem(136)};
	`,
	illustrative: styled.div`
		display: flex;
		justify-content: center;
		align-items: center;
		opacity: 0.3;
		margin: ${rem(8)} 0;
		padding: 0 25px;
	`,
	button: styled.div`
		display: flex;
		align-items: center;
		width: 100%;
		background: ${theme.primary};
		border-radius: ${rem(10)};
		min-height: ${rem(64)};
		padding-top: ${tokens.spacing.x2};
		padding-bottom: ${tokens.spacing.x2};
		color: #fff;
	`,
	icon: styled.div`
		margin: 0 ${rem(20)};
		width: ${rem(24)};
		height: ${rem(24)};
		display: flex;
		justify-content: center;
		align-items: center;
		font-size: ${rem(24)};
		color: #fff;
		flex-shrink: 0;
	`,
};

interface InnerProps {
	controller: Controller;
}

function Inner({ controller }: InnerProps): JSX.Element {
	const presentation = usePresentation(controller.presentation);

	const items = presentation.items.map((item) => <FlexibleRecentVisitsButtonIcon key={item.id} component={item} />);

	return (
		<VStack spacing={8}>
			{items.length > 0 ? (
				items
			) : (
				<elements.illustrative>
					<LegacyText>{withTranslation('PW.Component.Flexible.RecentVisits.Empty')}</LegacyText>
				</elements.illustrative>
			)}
		</VStack>
	);
}

export function FlexibleRecentVisits({ component }: Props): JSX.Element {
	const controller = useService(Controller);
	const presentation = usePresentation(controller.presentationAuthState);

	useEffect(() => {
		return () => {
			controller.teardown();
		};
	}, [controller]);

	return (
		<Fragment>
			{presentation.shouldRender ? (
				<elements.container data-testid='FlexibleRecentVisits'>
					<Text variant='bodySmallRegular'>{component.title}</Text>
					<Spacer vertical={rem(4)} />

					<elements.fixedSizeContainer>
						<Suspense
							fallback={
								<VStack spacing={8}>
									<Shimmer width={300} height={64} cornerRadius={8} />
									<Shimmer width={300} height={64} cornerRadius={8} opacity={0.6} />
								</VStack>
							}
						>
							<Inner controller={controller} />
						</Suspense>
					</elements.fixedSizeContainer>
				</elements.container>
			) : null}
		</Fragment>
	);
}

interface FlexibleRecentVisitsButtonIconProps {
	component: FlexibleButtonIconModel;
	__TEMP__overrideNavigation?: (url: string) => void;
}

function FlexibleRecentVisitsButtonIcon({
	component,
	__TEMP__overrideNavigation,
	...rest
}: FlexibleRecentVisitsButtonIconProps): JSX.Element {
	const renderIcon = () => {
		if (component.icon?.type == 'local') {
			return <Icon type={component.icon.origin as IconType} />;
		}
		if (component.icon?.type == 'svgUrl') {
			return <ExternalSvg imgSrc={component.icon.origin} size={24} />;
		}
		if (component.icon?.type == 'pngUrl') {
			return <img src={component.icon.origin} height={24} alt='' />;
		}
		return null;
	};

	return (
		<Routeable
			url={component.url}
			requiresAuthentication={component.requiresAuthentication}
			openWith={component.openUrlWith}
			{...rest}
		>
			<elements.button data-testid='FlexibleButtonIcon'>
				<elements.icon>{renderIcon()}</elements.icon>

				<VStack fluid={false}>
					<Text variant='bodyLargeRegular'>{component.title}</Text>
					{component.subtitle ? <Text variant='bodyRegular'>{component.subtitle}</Text> : null}
				</VStack>
			</elements.button>
		</Routeable>
	);
}

// biome-ignore lint/suspicious/noRedeclare: suppressed due to migration - should be fixed
interface Presentation {
	items: FlexibleButtonIconModel[];
}

@Scoped()
class Controller {
	public readonly presentationAuthState = Presentation.create({
		shouldRender: true,
	});
	public readonly presentation = Presentation.createConcurrent<Presentation>();
	private authStateDisposer: Disposer | null = null;

	constructor(
		private readonly recentReceptionsApiService: RecentReceptionsApiService,
		private readonly unitService: UnitService,
		private readonly authService: AuthService,
	) {
		this.load();

		this.authStateDisposer = this.authService.events.on('onSuccessfulAuthentication', () => {
			this.load();
		});
	}

	public teardown(): void {
		if (this.authStateDisposer) {
			this.authStateDisposer();
			this.authStateDisposer = null;
		}
	}

	private load(): void {
		if (this.authService.isAuthenticated()) {
			this.presentation
				.suspend(this.recentReceptionsApiService.getRecentlyVisited(2), this.parseBackendData.bind(this))
				.gracefulDegradation(() => {
					// TODO: This is not typed as it should be
					// eslint-disable-next-line @typescript-eslint/ban-ts-comment
					// @ts-ignore
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					// biome-ignore lint/suspicious/noExplicitAny: suppressed due to migration - should be fixed
					return Promise.resolve<any>({
						data: [].filter(Boolean),
					});
				});
			this.presentationAuthState.write({ shouldRender: true });

			return;
		}

		this.presentationAuthState.write({ shouldRender: false });
	}

	private parseBackendData({ data }: VisibaEnvelope<ReceptionVisitInput[]>): Presentation {
		return {
			items: data
				.map(({ ReceptionId }) => {
					const units = Array.from(this.unitService.units.values());
					const unit = units.find(({ receptionId }) => receptionId === ReceptionId);

					if (unit == null) return null;

					return new FlexibleButtonIconModel({
						id: ReceptionId,
						order: -1,
						title: unit.name,
						subtitle: '',
						url: getAbsoluteRoute(unit.route),
						openUrlWith: OpenUrlWithKind.SameTab,
						horizontalAlignment: HorizontalAlignment.End,
						icon: {
							origin: 'home2',
							type: 'local',
						},
						hasBrandedNavbar: false,
						openAnotherApp: false,
						requiresAuthentication: false,
						channel: null,
					});
				})
				.filter(Boolean) as FlexibleButtonIconModel[],
		};
	}
}
